import axios, { AxiosInstance, AxiosError } from "axios";
import { authService } from "./authService";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  "https://monu-cotacoes-api-fkgbfraqd4hqc7da.brazilsouth-01.azurewebsites.net/api";

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Flag para evitar múltiplas tentativas de refresh simultâneas
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Interceptor para adicionar token JWT de autenticação
apiClient.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Interceptor para tratamento de erros com refresh automático de token
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest: any = error.config;

    // Se receber 401 (não autorizado) e não for tentativa de login/refresh
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/wrike/token") &&
      !originalRequest.url?.includes("/auth/wrike/refresh")
    ) {
      // Se já está tentando fazer refresh, colocar requisição na fila
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Obter refresh token do localStorage
        const refreshToken = authService.getRefreshToken();

        if (!refreshToken) {
          throw new Error("Refresh token não encontrado");
        }

        // Renovar token - o authService já salva automaticamente no localStorage
        const refreshResponse = await authService.refreshToken(refreshToken);

        // Garantir que temos o novo token (já deve estar no localStorage)
        const newToken = refreshResponse.token;

        if (!newToken) {
          throw new Error("Token não retornado pelo refresh");
        }

        // Processar fila de requisições pendentes com o novo token
        processQueue(null, newToken);

        // Retentar requisição original com novo token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh falhou - processar fila com erro e fazer logout
        console.error("Falha ao renovar token:", refreshError);
        processQueue(refreshError, null);

        // Fazer logout
        try {
          await authService.logout();
        } catch (logoutError) {
          console.error("Erro ao fazer logout:", logoutError);
          // Limpar dados locais mesmo com erro
          localStorage.removeItem("auth_token");
          localStorage.removeItem("user");
        }

        // Redirecionar para login
        if (
          !window.location.pathname.includes("/login") &&
          !window.location.pathname.includes("/auth/")
        ) {
          window.location.href = "/login?error=token_expired";
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Outros erros
    return Promise.reject(error);
  },
);

export default apiClient;
